import { Component } from '@theme/component';

/**
 * Ola · Estado de la tienda (snippets/ola-estado-tienda.liquid).
 *
 * Con la hora de la tienda (la zona horaria de Shopify, en `data-offset`, por ejemplo «-0600»)
 * decide si está abierta entre `data-open` y `data-close` (horas de 0 a 24), escribe la etiqueta
 * que corresponde y marca `data-open` para el punto verde. Se actualiza cada minuto.
 * Llega con `hidden` y se muestra solo cuando ya tiene el estado.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} text - Donde va la etiqueta.
 *
 * @extends {Component<Refs>}
 */
export class OlaEstadoTienda extends Component {
  requiredRefs = ['text'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  #start() {
    window.clearInterval(this.#timer);
    this.#update();
    this.#timer = window.setInterval(() => this.#update(), 60_000);
  }

  #update() {
    const open = Number(this.dataset.open ?? 10);
    const close = Number(this.dataset.close ?? 20);
    const hour = storeHour(this.dataset.offset ?? '');

    const isOpen = close > open ? hour >= open && hour < close : hour >= open || hour < close;
    let label = this.dataset.labelOpen;
    if (!isOpen) label = hour < open ? this.dataset.labelToday : this.dataset.labelTomorrow;

    this.refs.text.textContent = label ?? '';
    this.toggleAttribute('data-open', isOpen);
    this.hidden = !label;
  }
}

/**
 * Hora actual de la tienda, con minutos como fracción (10:30 → 10.5).
 * @param {string} offset - Diferencia con UTC como la da Liquid: «-0600», «+0530».
 * @returns {number}
 */
function storeHour(offset) {
  const match = offset.match(/^([+-])(\d{2})(\d{2})$/);
  const minutes = match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : 0;
  const now = new Date(Date.now() + minutes * 60_000);
  return now.getUTCHours() + now.getUTCMinutes() / 60;
}

if (!customElements.get('ola-estado-tienda')) {
  customElements.define('ola-estado-tienda', OlaEstadoTienda);
}
